import * as Sentry from '@sentry/vue';
import { useServiceWorkerUpdates } from '@/composables/useServiceWorkerUpdates';

const ATTEMPT_STORAGE_KEY = 'zwm:chunk-recovery-attempt';
const ATTEMPT_WINDOW_MS = 60_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS for',
  'Loading CSS chunk',
  'Loading chunk',
  // Stale/404'd .wasm asset resolved to an HTML fallback body instead of real wasm bytes.
  'expected magic word'
];

/**
 * Detects the class of errors caused by a stale/deleted build asset (JS chunk, CSS
 * chunk, or wasm module) resolving to something other than its real content - usually
 * because a deploy replaced the hashed filenames a client still references.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR_PATTERNS.some((needle) => message.includes(needle));
}

function hasRecentAttempt(): boolean {
  const raw = sessionStorage.getItem(ATTEMPT_STORAGE_KEY);
  if (!raw) {
    return false;
  }

  try {
    const { timestamp } = JSON.parse(raw) as { timestamp: number };
    const withinWindow = Date.now() - timestamp < ATTEMPT_WINDOW_MS;
    if (!withinWindow) {
      // Don't let a much older entry linger for the rest of a (possibly months-long) tab session.
      sessionStorage.removeItem(ATTEMPT_STORAGE_KEY);
    }
    return withinWindow;
  } catch {
    sessionStorage.removeItem(ATTEMPT_STORAGE_KEY);
    return false;
  }
}

function recordAttempt(): void {
  sessionStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify({ timestamp: Date.now() }));
}

/** Clears the recovery guard early once a mount has been stable for a while. */
export function clearChunkRecoveryGuard(): void {
  sessionStorage.removeItem(ATTEMPT_STORAGE_KEY);
}

export interface IRecoverFromChunkLoadErrorOptions {
  /** Reload the page once the service worker check finishes. Defaults to true. */
  reload?: boolean;
}

/**
 * Single guarded recovery path for stale-chunk/asset failures. Forces a real
 * service-worker update check before optionally reloading, and never attempts more
 * than one recovery per `ATTEMPT_WINDOW_MS` so a permanent (non-stale-deploy) failure
 * can't cause a reload loop.
 */
export async function recoverFromChunkLoadError(
  error: unknown,
  options: IRecoverFromChunkLoadErrorOptions = {}
): Promise<void> {
  const { reload = true } = options;
  console.error('Recovering from chunk load error:', error);
  if (hasRecentAttempt()) {
    Sentry.captureException(error, { tags: { chunkRecovery: 'exhausted' } });
    return;
  }
  recordAttempt();

  let registration: ServiceWorkerRegistration | undefined;
  try {
    registration = await navigator.serviceWorker.getRegistration();
    await registration?.update();
  } catch (swError) {
    console.error('Failed to check for a service worker update during chunk recovery:', swError);
  }

  if (registration?.waiting) {
    const { updateServiceWorker } = useServiceWorkerUpdates();
    await updateServiceWorker(reload);
    return;
  }

  if (reload) {
    window.location.reload();
  }
}
